import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'v3/components';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import LinkActionsMenu from '../LinkActionsMenu';

function toDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateBR(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
}

function getLinkUrl(item) {
  if (item?.link?.url) return item.link.url;

  if (typeof item?.linkUrl === 'string') return item.linkUrl;
  if (typeof item?.publicUrl === 'string') return item.publicUrl;
  if (typeof item?.url === 'string') return item.url;

  if (item?.link?.token) {
    const base =
      import.meta.env.VITE_BASE_URL_PUBLIC_CHECKLIST ||
      import.meta.env.VITE_REDIRECR_URI ||
      'http://localhost:3000';
    return `${base}/checklist/public/${item.link.token}`;
  }

  return '';
}

function getLinkExpiresAt(item) {
  const days = Number(item?.link?.expirationDays ?? 0);
  if (!days) return null;

  const createdAt = toDate(item?.link?.createdAt || item?.link?.created_at);
  if (!createdAt) return null;

  const d = new Date(createdAt);
  d.setDate(d.getDate() + days);
  return d;
}

function buildLinkExpireLabel(item) {
  if (item?.linkIsClosed) return 'Encerrado';
  if (item?.link?.deletedAt) return 'Encerrado';
  if (item?.link?.isExpired) return 'Expirado';

  const expiresAt = getLinkExpiresAt(item);
  if (!expiresAt) return 'Sem expiração';

  return formatDateBR(expiresAt);
}

function countLinkResponses(item) {
  const groups = Array.isArray(item?.questionsResponses) ? item.questionsResponses : null;
  if (groups) {
    return groups.filter((g) => g?.answeredAt !== null).length;
  }
  const n = Number(item?.responsesCount ?? item?.answersCount ?? item?.responses ?? 0);
  if (Number.isFinite(n) && n >= 0) return n;
  return 0;
}

export default function LinkCard({
  item,
  styles,
  onCopyLink,
  onEditConfig,
  onCloseChecklist,
  onViewAnswers,
  viewAnswersLoading = false,
  showViewAnswers = true,
}) {
  const linkUrl = item?.linkUrl || getLinkUrl(item);
  const isConsumed = !!item?.isConsumedLink;
  const isClosed = !!item?.link?.deletedAt || !!item?.linkIsClosed || isConsumed;
  const isExpired = !!item?.link?.isExpired;

  const linkStatus = isClosed ? 'closed' : isExpired ? 'expired' : 'active';

  const canCopy = !!linkUrl && !isClosed;
  const inputValue = isClosed ? 'Link encerrado' : linkUrl;

  const [copied, setCopied] = useState(false);
  const tRef = useRef(null);

  useEffect(() => {
    return () => {
      if (tRef.current) window.clearTimeout(tRef.current);
    };
  }, []);

  async function onCopy() {
    if (!canCopy) return;
    await onCopyLink(linkUrl);
    setCopied(true);
    if (tRef.current) window.clearTimeout(tRef.current);
    tRef.current = window.setTimeout(() => setCopied(false), 1500);
  }

  const responsesCount = countLinkResponses(item);
  const originChecklistId =
    item?.originChecklistId ?? item?.originChecklist?.id ?? item?.checklistId ?? null;
  const originChecklistName =
    item?.originChecklistName || item?.originNameChecklist || item?.originChecklist?.name || '';

  return (
    <div style={styles.linkCard}>
      <div style={styles.linkHeaderRow}>
        <div style={styles.linkTitleRow}>
          <div style={styles.linkIconWrap}>
            <LinkOutlinedIcon style={{ fontSize: 18 }} />
          </div>

          <div style={{ minWidth: 0 }}>
            <div style={styles.linkTitleLine}>
              <span style={styles.linkTitle}>{`#${item.id} ${item.title}`}</span>
              <span style={styles.statusPill(linkStatus)}>{isClosed ? 'Encerrado' : isExpired ? 'Expirado' : 'Ativo'}</span>
            </div>
            <div style={styles.linkSubtitle}>{item.description}</div>
            {!!item?.plateDisplayLabel && (
              <div style={styles.linkPlateSummary}>{item.plateDisplayLabel}</div>
            )}
          </div>
        </div>

        <LinkActionsMenu
          styles={styles}
          onCopy={onCopy}
          onEdit={onEditConfig}
          onClose={onCloseChecklist}
          disableCopy={!canCopy}
          disableCopyTitle={isClosed ? 'Link encerrado' : 'Copiar Link'}
        />
      </div>

      <div style={styles.linkInfoGrid}>
        <div style={styles.infoStrip}>
          <div style={styles.infoTop}>
            <AssignmentTurnedInOutlinedIcon style={styles.infoIco} />
            Respostas
          </div>
          <div style={styles.infoBottom}>{responsesCount}</div>
        </div>

        <div style={styles.infoStrip}>
          <div style={styles.infoTop}>
            <PersonOutlineOutlinedIcon style={styles.infoIco} />
            Tipo
          </div>
          <div style={styles.infoBottom}>{item?.link?.isAnonymous ? 'Anônimo' : 'Identificado'}</div>
        </div>

        <div style={styles.infoStrip}>
          <div style={styles.infoTop}>
            <CalendarTodayOutlinedIcon style={styles.infoIco} />
            Criado em
          </div>
          <div style={styles.infoBottom}>{formatDateBR(item?.link?.createdAt || item?.createdAt)}</div>
        </div>

        <div style={styles.infoStrip}>
          <div style={styles.infoTop}>
            <AccessTimeOutlinedIcon style={styles.infoIco} />
            Expira em
          </div>
          <div style={styles.infoBottomExpire(linkStatus)}>{item?.expireLabel || buildLinkExpireLabel(item)}</div>
        </div>

        <div style={styles.infoStrip}>
          <div style={styles.infoTop}>
            <AssignmentTurnedInOutlinedIcon style={styles.infoIco} />
            Checklist Origem
          </div>
          <div style={styles.infoSub}>
            {originChecklistId ? (
              <a
                href={`/checklist/${originChecklistId}`}
                style={styles.originLink}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  window.open(`/checklist/${originChecklistId}`, '_blank', 'noopener,noreferrer');
                }}
              >
                {originChecklistId}
              </a>
            ) : (
              '—'
            )}
          </div>
          <div style={styles.infoBottom}>{originChecklistName || '—'}</div>
        </div>
      </div>

      <div style={styles.linkRow}>
        <input
          value={inputValue}
          readOnly
          disabled={isClosed}
          style={{
            ...styles.linkInput,
            ...(isClosed ? { userSelect: 'none', pointerEvents: 'none' } : null),
          }}
        />

        <button
          type="button"
          onClick={onCopy}
          disabled={!canCopy}
          style={
            !canCopy
              ? { ...styles.copyBtn, opacity: 0.45, cursor: 'not-allowed' }
              : copied
                ? styles.copyBtnCopied
                : styles.copyBtn
          }
          aria-label={!canCopy ? 'Link encerrado' : copied ? 'Link copiado' : 'Copiar link'}
          title={!canCopy ? 'Link encerrado' : copied ? 'Copiado!' : 'Copiar link'}
        >
          {copied ? (
            <CheckCircleOutlineOutlinedIcon style={styles.copyIconCopied} />
          ) : (
            <ContentCopyOutlinedIcon style={styles.copyIcon} />
          )}
        </button>
      </div>

      {showViewAnswers ? (
        <div style={styles.linkFooter}>
          <div style={{ flex: 1 }} />
          <Button
            variant="secondary"
            style={styles.viewAnswersBtn}
            onClick={onViewAnswers}
            disabled={responsesCount === 0 || viewAnswersLoading}
          >
            <div style={styles.viewAnswersInner}>
              <VisibilityOutlinedIcon style={{ fontSize: 18, color: '#374151' }} />
              {viewAnswersLoading ? 'Carregando...' : 'Ver Respostas'}
            </div>
          </Button>
        </div>
      ) : null}
    </div>
  );
}
